<template>

  <!-- Speech Schedule Component -->
    <SpeechSchedule 
      :baseUrl="baseUrl"
      @schedule-loaded="onScheduleLoaded"
    />

  <div class="container-fluid">


<!-- Filters -->
<div class="filters-bar">
  <!-- Wrapper for date inputs with "to" separator on mobile -->
  <div class="date-wrapper">
    <input type="date" :value="startDateInput" @input="updateStartDate($event)" />
    <span class="date-separator">to</span>
    <input type="date" :value="endDateInput" @input="updateEndDate($event)" />
  </div>

  <!-- Client dropdown -->
  <select class="col-2" v-model="selectedCompanyId" :disabled="isLoadingCompanies">
    <option value="">All Clients</option>
    <option v-for="company in companies" :key="company.companyId || company.id" :value="company.companyId || company.id">
      {{ company.companyName || company.name || company.Name || 'Unknown Client' }}
    </option>
  </select>
  <span v-if="isLoadingCompanies" class="loading-text">Loading clients...</span>

  <!-- Buttons group -->
  <div class="btn-group-wrapper">
   
    <button class="btn btn-primary btn-sm m-1" @click="OnAddJobCliked">
      <i class="bi bi-plus-circle"></i> Add Job
    </button>
    <button class="btn btn-danger btn-sm m-1" @click="toggleSelectMode()">
      <i class="bi" :class="selectMode ? 'bi-x-circle' : 'bi-check-circle'"></i>
      {{ selectMode ? 'Cancel' : 'Select Mode' }}
    </button>
    <button 
      v-if="selectMode && selectedJobIds.size > 0" 
      class="btn btn-danger btn-sm m-1"
      @click="deleteSelectedJobs()"
    >
      <i class="bi bi-trash-fill" style="color:black;"></i>
      <span class="text-yellow">Delete ({{ selectedJobIds.size }})</span>
    </button>
  </div>

  <!-- Adjustment checkbox -->
  <label class="adjustment-checkbox">
    <input type="checkbox" v-model="includeAdjustment" @change="getScheduleForToday()" />
    <span>⏱️ Include Adjustment Hours</span>
  </label>
</div>

    <!-- Drag Feedback -->
    <div v-if="isDragging" class="drag-feedback">
      <div class="drag-feedback-content">
        <span>📋 Creating jobs from {{ dragSourceDate }} to {{ dragTargetDate }} ({{ dateRangeCount }} days)</span>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isCreatingJobs" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div>Creating {{ jobsToCreate }} job(s)...</div>
    </div>

    <!-- Toast Notification System -->
    <div v-if="showToast" class="toast-container" :class="toastType">
      <div class="toast-content">
        <span class="toast-icon">{{ toastIcon }}</span>
        <span class="toast-message">{{ toastMessage }}</span>
        <button class="toast-close" @click="closeToast">×</button>
      </div>
    </div>

    <!-- Job Modal Component -->
    <JobModal 
      ref="jobModal"
      :baseUrl="baseUrl"
      :officers="officers"
      :sites="sites"
      :jobStatuses="jobStatuses"
      @job-added="getScheduleForToday"
      @job-updated="getScheduleForToday"
    />

    <!-- Not authorized -->
    <div>
      <span v-show="!isAuthorized" class="text-danger h2">Please make sure you are logged in.</span>
    </div>

    <!-- Grand Total Banner -->
    <div class="grand-total-banner" v-if="dayList.length > 0 && hasScheduleData">
      <span class="grand-total-icon">📊</span>
      <span class="grand-total-label">Grand Total:</span>
      <span class="grand-total-value">{{ grandTotalHours }}</span>
      <span class="grand-total-days">({{ dayList.length }} days)</span>
    </div>

<!-- GRID -->
<div class="schedule-grid" v-show="isAuthorized" :class="{ 'select-mode-active': selectMode }">
  <!-- Show when there are dates AND jobs -->
  <div class="timeline-wrapper" v-if="dayList.length > 0 && hasAnyJobs">

    <!-- Top Header -->
    <div class="grid-header">
      <div class="header-cell time-header">Time</div>
      <div v-for="day in dayList" :key="day.dateKey" class="header-cell day-header">
        <div class="day-date">
          <div class="day-full">{{ formatDayLabel(day.date) }}</div>
        </div>
        <div class="day-total">⏱️ {{ dailyTotalHours[day.dateKey] }}</div>
      </div>
    </div>

    <!-- Time Rows -->
    <div class="grid-body">
      <div v-for="hour in filteredHourList" :key="hour" class="grid-row">
        <div class="grid-cell time-cell">{{ formatHourLabel(hour) }}</div>

        <div 
          v-for="day in dayList" 
          :key="day.dateKey + '-' + hour" 
          class="grid-cell slot-cell"
          :class="{ 
            'drag-over': isDragOver && dragOverKey === `${day.dateKey}-${hour}`,
            'has-conflict': hasJobConflict(day, hour)
          }"
          @dragover.prevent="onDragOver(day, hour)"
          @dragleave="onDragLeave"
          @drop.prevent="onDrop(day, hour)"
        >
          <div
            v-for="duty in jobsForSlot(day, hour)"
            :key="duty.jobId"
            class="job-block"
            :class="[
              setStatusColor(duty.statusId), 
              { 'job-selected': selectMode && selectedJobIds.has(duty.jobId) }
            ]"
            :draggable="!selectMode"
            @click="selectMode ? toggleJobSelection(duty.jobId) : openEditJob(duty)"
            @dblclick="!selectMode && openEditJob(duty)"
            @dragstart="!selectMode && onDragStart($event, duty, day, hour)"
            @dragend="onDragEnd"
          >
            <div v-if="selectMode" class="job-checkbox">
              <input type="checkbox" :checked="selectedJobIds.has(duty.jobId)" @change.stop />
            </div>
            <div class="job-seq-circle">{{ jobSequenceMap[duty.jobId] }}</div>
            <div class="job-title" :title="duty.securityOfficerInfo">{{ formatOfficerName(duty.securityOfficerInfo, dayList.length) }}</div>
            <div class="job-site-name" :title="duty.siteInfo">{{ formatSiteName(duty.siteInfo, dayList.length) }}</div>
            <div class="job-meta">
              <span class="job-time" :title="getTimeTooltip(duty)">{{ formatTimeSpan(duty.startTime) }}–{{ formatTimeSpan(duty.endTime) }}</span>
              <span class="job-duration">{{ calculateJobDuration(duty.startTime, duty.endTime) }}</span>
              <span class="job-client" :title="duty.clientInfo">{{ formatClientName(duty.clientInfo, dayList.length) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Header -->
    <div class="grid-header bottom-header">
      <div class="header-cell time-header">Time</div>
      <div v-for="day in dayList" :key="'bottom-' + day.dateKey" class="header-cell day-header">
        <div class="day-date">
          <div class="day-full">{{ formatDayLabel(day.date) }}</div>
        </div>
        <div class="day-total">⏱️ {{ dailyTotalHours[day.dateKey] }}</div>
      </div>
    </div>

  </div>
  
  <!-- Show when there are dates but NO jobs -->
  <div v-else-if="dayList.length > 0 && !hasAnyJobs && isAuthorized && !isLoading" class="no-data">
    No jobs found for selected dates
  </div>
  
  <!-- Show when there are no dates in range -->
  <div v-else-if="dayList.length === 0 && isAuthorized && !isLoading" class="no-data">
    No schedule data available for selected dates
  </div>
  
  <div v-if="isLoading" class="loading">
    Loading schedule data...
  </div>
</div>

  </div>
</template>

<script lang="ts">
import { defineComponent, watch } from "vue";
import JobModal from "@/components/JobModal.vue";
import SpeechSchedule from '@/components/SpeechSchedule.vue'

interface ScheduleViewModel {
  jobId: number;
  siteId?: number;
  siteInfo?: string;
  siteDetail?: string;
  startTime?: any;
  endTime?: any;
  jobDate: string;
  payRate?: number;
  securityOfficerId?: number;
  securityOfficerInfo?: string;
  operator?: number;
  statusId?: number;
  statusInfo?: string;
  clientId?: number;
  clientInfo?: string;
  adjustmentHours?: number;
  bonusHours: number;
  travelExpense: number;
  remarks: string;
  rate: number;
}

interface JobUpdateViewModel {
  jobId?: number;
  siteId?: number;
  siteInfo?: string;
  siteDetail?: string;
  startTime: string;
  endTime: string;
  jobDate: string;
  securityOfficerId?: number;
  securityOfficerInfo?: string;
  operator?: number;
  statusId?: number;
  clientId?: number;
  clientInfo?: string;
  remarks?: string;
  adjustment?: number;
  rate?: number;
  travelExpense?: number;
  payRate?: number;
  bonusHours?: number;
  oldJobId?: number;
}

interface ScheduleViewModelDatewiseList {
  jobDate: string;
  scheduleViewModels: ScheduleViewModel[];
}

export default defineComponent({
  name: "ScheduleView",
  components: { JobModal , SpeechSchedule },
  props: { baseUrl: String },

  data() {
    return {
      includeAdjustment: false,
      schedule: {} as Record<string, ScheduleViewModelDatewiseList>,
      dayList: [] as Array<{ dateKey: string; date: Date }>,
      hourList: [...Array(24).keys()],
      isAuthorized: false,
      isLoading: false,
      isCreatingJobs: false,
      isLoadingCompanies: false,

      startDate: new Date(),
      endDate: new Date(),
      selectedCompanyId: "",

      companies: [] as Array<{ companyId: number; companyName: string }>,
      
      officers: [] as Array<any>,
      sites: [] as Array<any>,
      jobStatuses: [] as Array<any>,

      // Drag and drop state
      dragSourceJob: null as ScheduleViewModel | null,
      dragSourceDay: null as { dateKey: string; date: Date } | null,
      dragSourceHour: null as number | null,
      isDragging: false,
      isDragOver: false,
      dragOverKey: null as string | null,
      dragTargetDate: null as string | null,
      dragTargetHour: null as number | null,
      dragSourceDate: null as string | null,
      jobsToCreate: 0,
      dateRangeCount: 0,

      // Delete feature properties
      selectMode: false,
      selectedJobIds: new Set<number>(),

      // Toast notification system
      showToast: false,
      toastMessage: "",
      toastType: "success" as "success" | "error" | "info" | "warning",
      toastTimeout: null as number | null,

      // Abort controller for canceling requests
      abortController: null as AbortController | null,
    };
  },

  computed: {
    hasAnyJobs(): boolean {
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        if (dayData?.scheduleViewModels?.length > 0) {
          return true;
        }
      }
      return false;
    },
    
    hasScheduleData(): boolean {
      return Object.keys(this.schedule).length > 0;
    },

    toastIcon(): string {
      const icons: Record<string, string> = {
        success: "✅",
        error: "❌",
        warning: "⚠️",
        info: "ℹ️"
      };
      return icons[this.toastType] || "ℹ️";
    },

    jobSequenceMap(): Record<number, number> {
      const map: Record<number, number> = {};
      let counter = 1;
      for (const day of this.dayList) {
        for (let hour = 0; hour < 24; hour++) {
          const jobs = this.jobsForSlot(day, hour);
          for (const job of jobs) {
            map[job.jobId] = counter++;
          }
        }
      }
      return map;
    },
    
    dailyTotalHours(): Record<string, string> {
      const totals: Record<string, string> = {};
      
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        let totalMinutes = 0;
        
        if (dayData?.scheduleViewModels) {
          for (const job of dayData.scheduleViewModels) {
            totalMinutes += this.getDurationInMinutes(job.startTime, job.endTime, job.adjustmentHours);
          }
        }
        
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const roundedMinutes = Math.round(minutes * 100) / 100;
        
        totals[day.dateKey] = roundedMinutes === 0 ? `${hours}h` : `${hours}h ${roundedMinutes}m`;
      }
      return totals;
    },

    grandTotalHours(): string {
      let totalMinutes = 0;
      
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        if (dayData?.scheduleViewModels) {
          for (const job of dayData.scheduleViewModels) {
            totalMinutes += this.getDurationInMinutes(job.startTime, job.endTime, job.adjustmentHours);
          }
        }
      }
      
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const roundedMinutes = Math.round(minutes * 100) / 100;
      
      return roundedMinutes === 0 ? `${hours}h` : `${hours}h ${roundedMinutes}m`;
    },

    firstNonEmptyHour(): number {
      let earliestHour = 24;
      for (const day of this.dayList) {
        for (let hour = 0; hour < 24; hour++) {
          if (this.jobsForSlot(day, hour).length > 0 && hour < earliestHour) {
            earliestHour = hour;
          }
        }
      }
      return earliestHour === 24 ? 6 : earliestHour;
    },

    filteredHourList(): number[] {
      return this.hourList.filter(hour => {
        if (hour < this.firstNonEmptyHour) return false;
        return this.dayList.some(day => this.jobsForSlot(day, hour).length > 0);
      });
    },

    startDateInput(): string {
      return this.dateToYYYYMMDD(this.startDate);
    },
    endDateInput(): string {
      return this.dateToYYYYMMDD(this.endDate);
    },
  },

  watch: {
    selectedCompanyId() { this.getScheduleForToday(); },
    
    /*
    startDate(newDate) {
      if (newDate) {
        const normalized = new Date(newDate);
        normalized.setHours(0, 0, 0, 0);
        this.startDate = normalized;
        //this.getScheduleForToday();
      }
    },
    endDate(newDate) {
      if (newDate) {
        const normalized = new Date(newDate);
        normalized.setHours(23, 59, 59, 999);
        this.endDate = normalized;
        //this.getScheduleForToday();
      }
    },
    */
    includeAdjustment() { this.getScheduleForToday(); }
  },

  methods: {

    updateStartDate(event: any) {
    if (event.target.value) {
      const date = new Date(event.target.value);
      date.setHours(0, 0, 0, 0);
      this.startDate = date;
      this.getScheduleForToday();
    }
  },
  
  updateEndDate(event: any) {
    if (event.target.value) {
      const date = new Date(event.target.value);
      date.setHours(23, 59, 59, 999);
      this.endDate = date;
      this.getScheduleForToday();
    }
  },

  // Replace the onScheduleLoaded method with this:
 onScheduleLoaded(data: any) {
  //console.log('Schedule loaded from speech:', data);
  
  // Process the schedule data from speech
  if (data && data.schedule) {
    this.schedule = data.schedule;
    this.dayList = data.dates || [];
  }
  
  // Update startDate and endDate from speech
  if (data && data.startDate && data.endDate) {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    
    //console.log('Start date from speech:', start);
    //console.log('End date from speech:', end);
    
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      // Set proper time components
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      
      // Update the reactive properties
      this.startDate = new Date(start);
      this.endDate = new Date(end);
      
      // Force Vue to re-render the date inputs
      this.$forceUpdate();
      
      // Refresh the schedule with the new dates
      this.$nextTick(() => {
        this.getScheduleForToday();
      });
      
      /*console.log('Updated dates in parent:', {
        start: this.dateToYYYYMMDD(this.startDate),
        end: this.dateToYYYYMMDD(this.endDate)
      });
      */
    }
  }
},

    showNotification(message: string, type: "success" | "error" | "info" | "warning" = "info") {
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => this.closeToast(), 5000);
    },

    closeToast() {
      this.showToast = false;
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
        this.toastTimeout = null;
      }
    },

    hasJobConflict(day: { dateKey: string; date: Date }, hour: number): boolean {
      if (!this.dragSourceJob || !this.dragSourceDay) return false;
      const existingJobs = this.jobsForSlot(day, hour);
      return existingJobs.some(job => job.jobId !== this.dragSourceJob?.jobId);
    },

    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) this.selectedJobIds.clear();
    },

    toggleJobSelection(jobId: number) {
      this.selectedJobIds.has(jobId) 
        ? this.selectedJobIds.delete(jobId) 
        : this.selectedJobIds.add(jobId);
    },

    async deleteSelectedJobs() {
      if (this.selectedJobIds.size === 0) {
        this.showNotification("No jobs selected for deletion.", "warning");
        return;
      }
      
      if (!confirm(`Are you sure you want to delete ${this.selectedJobIds.size} job(s)?\n\nThis action cannot be undone.`)) {
        return;
      }
      
      try {
        const jobIdsArray = Array.from(this.selectedJobIds).map(id => ({ Id: id }));
        const response = await fetch(this.baseUrl + "schedule/DeleteJobs", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(jobIdsArray)
        });
        
        if (response.ok) {
          this.selectedJobIds.clear();
          this.selectMode = false;
          await this.getScheduleForToday();
          this.showNotification(`${jobIdsArray.length} job(s) deleted successfully!`, "success");
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to delete jobs: ${errorText}`);
        }
      } catch (error) {
        console.error("Error deleting jobs:", error);
        this.showNotification("Failed to delete jobs. Please try again.", "error");
      }
    },

    getTimeTooltip(duty: ScheduleViewModel) {
      const date = duty.jobDate ? new Date(duty.jobDate).toLocaleDateString() : 'Unknown date';
      return `${date} ${this.formatTimeSpan(duty.startTime)}–${this.formatTimeSpan(duty.endTime)}`;
    },

    getAllLookupData() {
      this.isLoadingCompanies = true;
      
      try {
        // Read from localStorage (cached by login)
        const cachedData = localStorage.getItem("lookupData");
        
        if (!cachedData) {
          this.showNotification("Please login again to load data.", "warning");
          this.isAuthorized = false;
          this.isLoadingCompanies = false;
          return;
        }
        
        const data = JSON.parse(cachedData);
        
        this.officers = data.officers || [];
        this.sites = data.sites || [];
        this.jobStatuses = data.jobStatuses || [];
        
        let companiesData = data.companies || [];
        if (companiesData.data && Array.isArray(companiesData.data)) {
          companiesData = companiesData.data;
        }
        if (companiesData.values && Array.isArray(companiesData.values)) {
          companiesData = companiesData.values;
        }
        
        this.companies = (Array.isArray(companiesData) ? companiesData : []).map((company: any) => ({
          companyId: company.companyId || company.id || company.Id || company.companyID || 0,
          companyName: company.companyName || company.name || company.Name || company.companyName || 'Unknown'
        }));
        
        this.isAuthorized = true;
        
      } catch (error) {
        console.error("Error loading lookup data:", error);
        this.showNotification("Failed to load client data.", "error");
      } finally {
        this.isLoadingCompanies = false;
      }
    },

    parseTimeToMinutes(timeValue: any): number {
      if (!timeValue) return 0;
      if (typeof timeValue === 'string') {
        const parts = timeValue.split(':');
        return (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1] || '0') || 0);
      }
      if (typeof timeValue === 'object') {
        return (timeValue.Hours || 0) * 60 + (timeValue.Minutes || 0);
      }
      return 0;
    },
    
    formatDayLabel(date: Date) {
      if (!date || isNaN(date.getTime())) return "Invalid";
      return date.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short"
      });
    },

    formatHourLabel(hour: number) {
      return `${hour.toString().padStart(2, "0")}:00`;
    },

    formatTimeSpan(timeSpan: any) {
      if (!timeSpan) return "N/A";
      if (typeof timeSpan === 'string') return timeSpan.substring(0, 5);
      if (typeof timeSpan === 'object') {
        const hours = timeSpan.Hours?.toString().padStart(2, '0') || '00';
        const minutes = timeSpan.Minutes?.toString().padStart(2, '0') || '00';
        return `${hours}:${minutes}`;
      }
      return "N/A";
    },

    formatOfficerName(name: string, dayCount: number) {
      if (!name) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 25 : 15;
      if (name.length <= maxLength) return name;
      
      const parts = name.trim().split(' ');
      if (parts.length === 1) return parts[0].substring(0, maxLength - 2) + '..';
      return `${parts[0]} ${parts[1]?.charAt(0)}.`;
    },

    formatSiteName(site: string, dayCount: number) {
      if (!site) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 25 : 15;
      const fullName = site.split(' - ')[0];
      return fullName.length <= maxLength ? fullName : fullName.substring(0, maxLength - 2) + '..';
    },

    formatClientName(client: string, dayCount: number) {
      if (!client) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 20 : 10;
      const fullName = client.split(' ')[0];
      return fullName.length <= maxLength ? fullName : fullName.substring(0, maxLength - 2) + '..';
    },

    jobsForSlot(day: { dateKey: string; date: Date }, hour: number) {
      if (!day?.date) return [];
      const dayData = this.schedule[day.dateKey];
      if (!dayData?.scheduleViewModels) return [];
      
      return dayData.scheduleViewModels.filter((duty: ScheduleViewModel) => {
        if (!duty.startTime) return false;
        
        if (duty.jobDate) {
          const jobDateObj = new Date(duty.jobDate);
          const columnDateObj = new Date(day.date);
          if (jobDateObj.toDateString() !== columnDateObj.toDateString()) return false;
        }
        
        let startHour = 0;
        if (typeof duty.startTime === 'string') startHour = parseInt(duty.startTime.substring(0, 2));
        else if (typeof duty.startTime === 'object' && duty.startTime.Hours !== undefined) {
          startHour = duty.startTime.Hours;
        }
        return startHour === hour;
      });
    },

    getDateKey(date: Date): string {
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}T00:00:00`;
    },

    setStatusColor(statusId: any) {
      return {
        'status-completed': statusId == 2,
        'status-in-progress': statusId == 4,
        'status-pending': statusId == 3,
        'status-cancelled': statusId == 8
      };
    },

    dateToYYYYMMDD(d: Date) {
      if (!d) return "";
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split("T")[0];
    },

    calculateJobDuration(startTime: any, endTime: any): string {
      if (!startTime || !endTime) return "0h";
      
      const startTotalMinutes = this.parseTimeToMinutes(startTime);
      const endTotalMinutes = this.parseTimeToMinutes(endTime);
      
      let durationMinutes = endTotalMinutes - startTotalMinutes;
      if (durationMinutes < 0) durationMinutes = 1440 + durationMinutes;
      
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
    },

    getDurationInMinutes(startTime: any, endTime: any, adjustmentHours = 0): number {
      if (!startTime || !endTime) return 0;
      
      const startTotalMinutes = this.parseTimeToMinutes(startTime);
      const endTotalMinutes = this.parseTimeToMinutes(endTime);
      
      let duration = endTotalMinutes - startTotalMinutes;
      if (duration < 0) duration = 1440 + duration;
      if (this.includeAdjustment) duration += (adjustmentHours || 0) * 60;
      
      return duration;
    },

    getScheduleForToday() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.isLoading = true;
      
      let companyId = 0;
      if (this.selectedCompanyId) {
        const parsed = parseInt(this.selectedCompanyId);
        if (!isNaN(parsed) && parsed > 0) companyId = parsed;
      }

      fetch(this.baseUrl + "schedule/ScheduleDictionaryForDateRangePageWise", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          StartDate: this.startDate,
          EndDate: this.endDate,
          PageNumber: 0,
          RecordsPerPage: 5000,
          SearchString: "",
          CompanyId: companyId
        }),
        signal: this.abortController.signal
      })
      .then(r => {
        if (r.status === 401) {
          this.isAuthorized = false;
          this.isLoading = false;
          this.showNotification("Session expired. Please login again.", "warning");
          return null;
        }
        this.isAuthorized = true;
        return r.json();
      })
      .then(result => {
        this.isLoading = false;
        if (!result) return;

        const scheduleData = result.scheduleSinglePageResult;
        if (!scheduleData) {
          this.schedule = {};
          this.dayList = [];
          return;
        }

        this.schedule = scheduleData;

        const dayListArray: Array<{ dateKey: string; date: Date }> = [];
        const currentDate = new Date(this.startDate);
        const endDateObj = new Date(this.endDate);
        currentDate.setHours(0, 0, 0, 0);
        endDateObj.setHours(0, 0, 0, 0);

        while (currentDate <= endDateObj) {
          dayListArray.push({
            dateKey: this.getDateKey(currentDate),
            date: new Date(currentDate)
          });
          currentDate.setDate(currentDate.getDate() + 1);
        }

        this.dayList = dayListArray.sort((a, b) => a.date.getTime() - b.date.getTime());
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        this.isLoading = false;
        console.error("Error fetching schedule:", error);
        this.showNotification("Failed to load schedule data.", "error");
      });
    },

    OnAddJobCliked() {
      (this.$refs.jobModal as any).openForAdd();
    },

    openEditJob(duty: ScheduleViewModel) {
      (this.$refs.jobModal as any).openForEdit(duty);
    },

    onDragStart(event: DragEvent, duty: ScheduleViewModel, day: { dateKey: string; date: Date }, hour: number) {
      this.dragSourceJob = duty;
      this.dragSourceDay = day;
      this.dragSourceHour = hour;
      this.isDragging = true;
      this.dragSourceDate = this.formatDayLabel(day.date);
      
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('text/plain', JSON.stringify({
          jobId: duty.jobId,
          dateKey: day.dateKey,
          hour: hour
        }));
      }
      event.stopPropagation();
    },

    onDragEnd() {
      this.dragSourceJob = null;
      this.dragSourceDay = null;
      this.dragSourceHour = null;
      this.isDragging = false;
      this.isDragOver = false;
      this.dragOverKey = null;
      this.dragTargetDate = null;
      this.dragTargetHour = null;
      this.dragSourceDate = null;
      this.dateRangeCount = 0;
      this.jobsToCreate = 0;
    },

    onDragOver(day: { dateKey: string; date: Date }, hour: number) {
      this.isDragOver = true;
      this.dragOverKey = `${day.dateKey}-${hour}`;
      this.dragTargetDate = this.formatDayLabel(day.date);
      this.dragTargetHour = hour;
      
      if (this.dragSourceDay) {
        const startDate = new Date(this.dragSourceDay.date);
        const endDate = new Date(day.date);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        this.dateRangeCount = diffDays + 1;
        this.jobsToCreate = this.dateRangeCount;
      }
    },

    onDragLeave() {
      this.isDragOver = false;
      this.dragOverKey = null;
    },

    async onDrop(day: { dateKey: string; date: Date }, targetHour: number) {
      this.isDragOver = false;
      
      if (!this.dragSourceJob || !this.dragSourceDay) {
        this.onDragEnd();
        return;
      }
      
      const existingJobs = this.jobsForSlot(day, targetHour);
      const hasConflict = existingJobs.some(job => job.jobId !== this.dragSourceJob?.jobId);
      
      if (hasConflict) {
        if (!confirm(`There are already ${existingJobs.length} job(s) at this time. Create additional jobs anyway?`)) {
          this.onDragEnd();
          return;
        }
      }
      
      const isSameDay = this.dragSourceDay.dateKey === day.dateKey;
      const isSameHour = this.dragSourceHour === targetHour;
      
      const startDate = new Date(this.dragSourceDay.date);
      const endDate = new Date(day.date);
      const fromDate = startDate <= endDate ? startDate : endDate;
      const toDate = startDate <= endDate ? endDate : startDate;
      
      const dateRange: Date[] = [];
      const currentDate = new Date(fromDate);
      while (currentDate <= toDate) {
        dateRange.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      let datesToCreate: Date[] = [];
      const sourceDateStr = this.dateToYYYYMMDD(this.dragSourceDay.date);
      
      if (isSameDay) {
        datesToCreate = [new Date(day.date)];
      } else {
        datesToCreate = dateRange.filter(date => this.dateToYYYYMMDD(date) !== sourceDateStr);
      }
      
      if (datesToCreate.length === 0) {
        this.showNotification("No new jobs to create.", "info");
        this.onDragEnd();
        return;
      }
      
      const durationMinutes = this.getDurationInMinutes(
        this.dragSourceJob.startTime, 
        this.dragSourceJob.endTime
      );
      
      const newStartTime = this.formatTimeString(targetHour, 0);
      const newEndTime = this.formatTimeString(
        targetHour + Math.floor(durationMinutes / 60), 
        durationMinutes % 60
      );
      
      let currentUserId: number | undefined;
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const userId = tokenPayload.nameid || tokenPayload.userId || tokenPayload.sub;
          if (userId) {
            currentUserId = typeof userId === 'number' ? userId : parseInt(userId);
            if (isNaN(currentUserId as number)) currentUserId = undefined;
          }
        }
      } catch (e) {
        console.warn("Could not decode token:", e);
      }
      
      if (!currentUserId) {
        const userIdFromStorage = localStorage.getItem("userId");
        if (userIdFromStorage) {
          currentUserId = parseInt(userIdFromStorage);
          if (isNaN(currentUserId)) currentUserId = undefined;
        }
      }
      
      let confirmMessage = "";
      if (isSameDay) {
        if (isSameHour) {
          confirmMessage = `Create a DUPLICATE job at the SAME time (${targetHour}:00) on ${this.formatDayLabel(day.date)}?\n\nOriginal job will remain unchanged.`;
        } else {
          confirmMessage = `Create a NEW job at ${targetHour}:00 on the SAME DAY (${this.formatDayLabel(day.date)})?\n\nOriginal job at ${this.dragSourceHour}:00 will remain unchanged.`;
        }
      } else {
        confirmMessage = `Create ${datesToCreate.length} job(s) for "${this.dragSourceJob.securityOfficerInfo}"\nFrom: ${this.formatDayLabel(fromDate)} at ${this.dragSourceHour}:00\nTo: ${this.formatDayLabel(toDate)} at ${targetHour}:00?\n\nThe original job on ${this.formatDayLabel(this.dragSourceDay.date)} will remain unchanged.`;
      }
      
      if (!confirm(confirmMessage)) {
        this.onDragEnd();
        return;
      }
      
      this.isCreatingJobs = true;
      this.jobsToCreate = datesToCreate.length;
      
      try {
        const jobsToCreateList: JobUpdateViewModel[] = [];
        for (const date of datesToCreate) {
          jobsToCreateList.push({
            siteId: this.dragSourceJob.siteId,
            siteInfo: this.dragSourceJob.siteInfo,
            siteDetail: this.dragSourceJob.siteDetail,
            startTime: newStartTime,
            endTime: newEndTime,
            jobDate: this.dateToYYYYMMDD(date),
            securityOfficerId: this.dragSourceJob.securityOfficerId,
            securityOfficerInfo: this.dragSourceJob.securityOfficerInfo,
            operator: currentUserId,
            statusId: this.dragSourceJob.statusId,
            clientId: this.dragSourceJob.clientId,
            clientInfo: this.dragSourceJob.clientInfo,
            remarks: this.dragSourceJob.remarks,
            adjustment: this.dragSourceJob.adjustmentHours,
            rate: this.dragSourceJob.rate,
            travelExpense: this.dragSourceJob.travelExpense,
            bonusHours: this.dragSourceJob.bonusHours,
            payRate: this.dragSourceJob.payRate,
            oldJobId: undefined
          });
        }
        
        const response = await fetch(this.baseUrl + "schedule/AddJobs", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(jobsToCreateList)
        });
        
        if (response.ok) {
          await this.getScheduleForToday();
          this.showNotification(`${jobsToCreateList.length} job(s) created successfully!`, "success");
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to create jobs: ${errorText}`);
        }
      } catch (error) {
        console.error("Error creating jobs:", error);
        this.showNotification("Failed to create jobs. Please try again.", "error");
      } finally {
        this.isCreatingJobs = false;
        this.onDragEnd();
      }
    },

    formatTimeString(hours: number, minutes: number): string {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    },
  },

  mounted() {
    this.isAuthorized = !!localStorage.getItem("token");
    this.getAllLookupData();
    this.getScheduleForToday();
  },

  beforeUnmount() {
    if (this.abortController) this.abortController.abort();
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
  }
});
</script>

<style scoped>
* { box-sizing: border-box; }

/* Layout */
.filters-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.filters-bar input,
.filters-bar select {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.filters-bar select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.loading-text {
  font-size: 12px;
  color: #6c757d;
  margin-left: 5px;
}

/* Grand Total Banner */
.grand-total-banner {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px 20px;
  margin-bottom: 20px;
  font-size: 18px;
  color: white;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.grand-total-icon { font-size: 24px; }
.grand-total-label { font-weight: 500; opacity: 0.9; }
.grand-total-value {
  font-size: 28px;
  font-weight: 700;
  padding: 4px 12px;
  background: rgba(255,255,255,0.2);
  border-radius: 25px;
}
.grand-total-days { font-size: 14px; opacity: 0.8; }

/* Schedule Grid */
.schedule-grid {
  width: 100%;
  overflow-x: auto;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.timeline-wrapper { min-width: 800px; }

.grid-header {
  display: grid;
  grid-template-columns: 80px repeat(v-bind(dayList.length), 1fr);
  position: sticky;
  top: 0;
  z-index: 10;
  background: #f5f5f5;
  border-bottom: 2px solid #ddd;
}

.bottom-header {
  top: auto;
  bottom: 0;
  border-top: 2px solid #ddd;
  border-bottom: none;
}

.header-cell {
  padding: 12px 8px;
  font-weight: 600;
  font-size: 13px;
  text-align: center;
  border-right: 1px solid #e0e0e0;
}

.time-header {
  font-weight: 700;
  background: #e8e8e8;
}

.day-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
}
.day-header:not(:last-child) { border-right: 2px dashed #dee2e6; }

.day-full {
  font-size: 16px;
  font-weight: 700;
  color: #2c3e50;
  text-transform: uppercase;
}

.day-total {
  display: inline-block;
  margin-top: 8px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #28a745;
  background: #e8f5e9;
  border-radius: 20px;
}

/* Grid Body */
.grid-body { display: flex; flex-direction: column; }

.grid-row {
  display: grid;
  grid-template-columns: 80px repeat(v-bind(dayList.length), 1fr);
  min-height: 85px;
  border-bottom: 1px solid #eee;
}

.grid-cell {
  padding: 6px;
  min-height: 85px;
  overflow: hidden;
  border-right: 1px solid #eee;
}

.time-cell {
  padding-top: 12px;
  font-size: 12px;
  font-weight: 900;
  color: #666;
  text-align: center;
  background: #fafafa;
}

.slot-cell {
  background: white;
  overflow: visible;
  transition: background-color 0.2s ease;
}
.slot-cell.drag-over {
  background-color: #e3f2fd;
  border: 2px dashed #2196f3;
}
.slot-cell.has-conflict {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
}

/* Job Blocks */
.job-block {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  padding: 6px 6px;
  margin-bottom: 4px;
  font-size: 10px;
  color: white;
  word-wrap: break-word;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  background: #2c3e50;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
}
.job-block:active { cursor: grabbing; }
.job-block:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

.job-block .job-title,
.job-block .job-site-name,
.job-block .job-meta {
  margin-right: 18px;
}
.job-block:not(.job-selected) .job-title,
.job-block:not(.job-selected) .job-site-name,
.job-block:not(.job-selected) .job-meta {
  margin-right: 0;
}

.job-title {
  font-size: 10px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-site-name {
  font-size: 9px;
  font-weight: 500;
  color: #ffd966;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-meta {
  display: flex;
  justify-content: space-between;
  gap: 4px;
  font-size: 8px;
}

.job-time { font-weight: 600; color: #fff; white-space: nowrap; flex-shrink: 0; }
.job-duration {
  font-weight: 600;
  color: #ffd966;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 0 4px;
  background: rgba(0,0,0,0.2);
  border-radius: 3px;
}
.job-client {
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  text-align: right;
  min-width: 0;
}

/* Status Colors */
.status-completed { background: #28a745; border-left: 3px solid #1e7e34; }
.status-in-progress {
  background: #ffc107;
  color: #333;
  border-left: 3px solid #d39e00;
}
.status-in-progress .job-site-name { color: #b8860b; }
.status-in-progress .job-time { color: #fff; }
.status-pending { background: #dc3545; border-left: 3px solid #a71d2a; }
.status-cancelled {
  background: #6c757d;
  opacity: 0.7;
  border-left: 3px solid #545b62;
}

/* Job Sequence Circle */
.job-seq-circle {
  position: absolute;
  top: 1px;
  left: 1px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 9px;
  font-weight: bold;
  color: #2c3e50;
  background: #ffc107;
  border-radius: 50%;
}

/* Select Mode */
.select-mode-active .job-block { cursor: pointer; }
.select-mode-active .job-block .job-title,
.select-mode-active .job-block .job-site-name,
.select-mode-active .job-block .job-meta {
  margin-right: 18px;
}

.job-selected {
  background: #dc3545 !important;
  opacity: 0.9;
  border: 2px solid #dc3545;
}

.job-checkbox {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 2;
  padding: 2px;
  background: rgba(255,255,255,0.95);
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.job-checkbox input {
  width: 14px;
  height: 14px;
  margin: 0;
  cursor: pointer;
}

/* Loading States */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  background: rgba(0,0,0,0.7);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  margin-bottom: 20px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data, .loading {
  padding: 40px;
  font-size: 14px;
  text-align: center;
  color: #666;
}
.loading { color: #007bff; }

/* Drag Feedback */
.drag-feedback {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  animation: slideIn 0.3s ease;
}
.drag-feedback-content {
  padding: 10px 15px;
  font-size: 14px;
  color: white;
  background: #2196f3;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  min-width: 300px;
  max-width: 400px;
  animation: slideInRight 0.3s ease;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #17a2b8;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.toast-container.success .toast-content { border-left-color: #28a745; }
.toast-container.error .toast-content { border-left-color: #dc3545; }
.toast-container.warning .toast-content { border-left-color: #ffc107; }
.toast-container.info .toast-content { border-left-color: #17a2b8; }

.toast-icon { font-size: 24px; }
.toast-message { flex: 1; font-size: 14px; color: #333; }
.toast-close {
  padding: 0 4px;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  background: none;
  border: none;
}
.toast-close:hover { color: #333; }

@keyframes slideInRight {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

/* Scrollbar */
.timeline-wrapper::-webkit-scrollbar { height: 8px; }
.timeline-wrapper::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.timeline-wrapper::-webkit-scrollbar-thumb { background: #888; border-radius: 4px; }
.timeline-wrapper::-webkit-scrollbar-thumb:hover { background: #555; }

.job-title,
.job-site-name,
.job-client {
  display: block;
  margin-bottom: 4px;
}

/* Responsive */
@media (max-width: 1200px) {
  .job-block { padding: 3px 4px; font-size: 9px; }
  .job-title { font-size: 9px; }
  .job-site-name { font-size: 8px; }
}

@media (max-width: 1024px) {
  .grid-header, .grid-row {
    grid-template-columns: 70px repeat(v-bind(dayList.length), minmax(120px, 1fr));
  }
}

@media (max-width: 768px) {
  .filters-bar { flex-direction: column; align-items: stretch; }
  .filters-bar input, .filters-bar button { width: 100%; }
  .job-block { font-size: 8px; }
  .job-title, .job-site-name { font-size: 8px; }
  .toast-container {
    top: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
  }
}

/* Minimal responsive fixes - only for mobile */
@media (max-width: 768px) {
  .filters-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-bar input,
  .filters-bar select {
    width: 100%;
  }

  /* Date wrapper with "to" separator */
  .date-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }
  
  .date-wrapper input[type="date"] {
    flex: 1;
    min-width: 0;
  }
  
  .date-separator {
    flex-shrink: 0;
    font-weight: bold;
    color: #6c757d;
    padding: 0 4px;
  }

  /* Buttons group - wraps nicely on mobile */
  .btn-group-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    width: 100%;
  }
  
  .btn-group-wrapper .btn {
    flex: 1;
    min-width: 60px;
    margin: 2px !important;
    font-size: 12px;
    padding: 6px 8px;
    white-space: nowrap;
  }

  /* Adjustment checkbox */
  .adjustment-checkbox {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    font-size: 13px;
    background: #e9ecef;
    border-radius: 4px;
    width: 100%;
    justify-content: center;
  }

  .adjustment-checkbox input[type="checkbox"] {
    width: 16px;
    height: 16px;
    margin: 0;
  }
}

/* For tablets and small desktops - keep original behavior */
@media (min-width: 769px) and (max-width: 1024px) {
  .filters-bar {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .filters-bar input[type="date"] {
    width: auto;
    flex: 0 1 auto;
  }
  
  .filters-bar select {
    width: auto;
    flex: 0 1 150px;
  }
  
  .btn-group-wrapper {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .btn-group-wrapper .btn {
    flex: 0 1 auto;
  }
}

</style>